import NavigationBar from "../comps/NavigationBar/NavigationBar";
import Footer from "../comps/Footer/Footer";
import ContentCard from "../comps/ContentCard/ContentCard";
import Sidebar from "../comps/Sidebar/Sidebar";

export default function Proof() {
  const menu = [
    {
      name: "Validation of the Model's Predictive Capability",
      id: "Proof-0",
    },
    {
      name: "Wet Lab Validation",
      id: "Proof-1",
    },
    {
      name: "Online Platform Usage Test",
      id: "Proof-2",
    },
    {
      name: "Command line operation",
      id: "Proof-3",
    },
  ];
  return (
    <div className="general-background">
      <NavigationBar />
      <Sidebar menu={menu} />
      <div className="main-container">
        <p className="title">Proof of Concept:</p>
        <ContentCard
          content={` 
            <p>
            In summary, we have verified the excellent performance of BioRocket through several aspects: validation of the model's predictive capability, wet lab validation, online platform usage test and command line operation test.
            </p>
            <br/>
            <p>
            Based on this evidence, we can confidently assert that BioRocket is a reliable tool for mining and analyzing eCISs.
            </p>
              `}
        />
        <p className="subtitle" key={"Proof-0"} id="Proof-0">
          • Validation of the Model's Predictive Capability:
        </p>
        <ContentCard
          content={` 
            <p>
            To assess the model's performance and generalization ability, we used 70% of the dataset (comprising both positive and negative samples) for training, while the remaining 30%, which was not part of the training process, formed the test set for evaluating the model's performance.
            </p>
            <br/>
            <p>
            The evaluation metrics selected were Accuracy, Precision, and Recall, which are commonly used model evaluation scores.
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/proof-figure1.png'
            alt='Figure 1. Model evaluation metrics'
            class='content-figure'
            style='width:42%'
            />
            <p class='content-legend'>
            Figure 1. Model evaluation metrics
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/proof-figure2.png'
            alt='Figure 2. Prediction models for different afp homologous proteins'
            class='content-figure'
            style='width:52%'
            />
            <p class='content-legend'>
            Figure 2. Prediction models for different afp homologous proteins
            </p>
            <br/>
            <p>
            From Figure 2, it can be observed that all the models exhibit strong predictive capabilities for input samples.
            </p>
              `}
        />
        <p className="subtitle" key={"Proof-1"} id="Proof-1">
          • Wet Lab Validation:
        </p>
        <ContentCard
          content={` 
            <p>
            Researchers have demonstrated that the region near the 3'end of eCIS gene clusters often contains toxin proteins, known as eCIS-associated toxins (EATs)[1]. In nature, these toxins are loaded and injected as cargo molecules by eCISs.
            </p>
            <br/>
            <p>
            To validate the reliability of eCIS data in the BioRocket database, we designed experiments to verify whether the EATs genes in certain eCISs can exhibit toxin functionality in the model organism (Escherichia coli).
            </p>
            <br/>
            <p>
            BioRocket contains thousands of eCISs, so we initially conducted a series of analyses on the data to identify representative systems with EATs genes that are more reliable for validation.
            </p>
            <br/>
            <p>
            Our effector gene screening process consists of several steps:
            </p>
            <p>
            1. Preliminary Gene Selection: In our initial phase of gene selection, we opted for a systematic approach based on gene annotations and blastp analysis. To streamline and expedite this gene screening process, we wrote a crawler code. This code was designed to perform continuous protein sequence blasting, enhancing the efficiency of our gene selection.
            </p>
            <p>
            2. Protein Domain Analysis: Subsequently, we conducted a thorough analysis of protein domains using Pfam.
            </p>
            <p>
            3. Structure Prediction: To gain insights into the possible effector genes, we employed AlphaFold to predict their 3D structures.
            </p>
            <p>
            4. Functional Assessment: The predicted protein structures (PDB files) were submitted to the Dali server, which facilitated the determination of potential functions based on the 3D structures of these genes.
            </p>
            <br/>
            <p>
            This systematic workflow enables the identification of effector genes with toxin potential.
            </p>
            <p>
            In the end, we selected three potential EATs genes, 12-HicA gene (protein_id=EAR22022.1), YoeB gene (protein_id=EXI67928.1), and PIN gene (protein_id=OJV26544.1), for validation from eCISs originating from three species: Nitrococcus mobilis Nb-231 (GCA_000153205.1), Candidatus Accumulibacter adjunctus (GCA_000585015.1), and Actinobacteria bacterium 69-20 (GCA_001897425.1).
            </p>
            <br/>
            <p>
            With TSINGKE's assistance in synthesizing and molecular cloning of these genes, we successfully expressed them in DH5-alpha, validating their status as eCIS effector genes.
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/proof-figure3.png'
            alt='Figure 3. Predictive 3D structure of 12-HicA toxin by Alphafold.'
            class='content-figure'
            style='width:60%'
            />
            <p class='content-legend'>
            Figure 3. Predictive 3D structure of 12-HicA toxin by Alphafold.
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/proof-figure4.png'
            alt='Figure 4. Predicted IDDT of 12-HicA toxin[2].  IDDT is a local superposition-free score used to compare protein structures and models based on distance difference tests. It complements pLDDT scores, which indicate model confidence in protein structure predictions[3].'
            class='content-figure'
            style='width:60%'
            />
            <p class='content-legend'>
            Figure 4. Predicted IDDT of 12-HicA toxin[2].  IDDT is a local superposition-free score used to compare protein structures and models based on distance difference tests. It complements pLDDT scores, which indicate model confidence in protein structure predictions[3].
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/proof-figure5.png'
            alt='Figure 5. Predicted Alignment Error (PAE) of the top five predicted structure of 12-HicA toxin. PAE estimates the relative positions of structural domains. The color at each point in the PAE plot represents the expected positional error of a residue on the predicted structure when aligned with a residue on the true structure. Lower PAE values indicate more accurate predictions of relative positions, while higher PAE values suggest uncertainty in the relative positions of residues from different structural domains in the 3D structure.'
            class='content-figure'
            style='width:70%'
            />
            <p class='content-legend'>
            Figure 5. Predicted Alignment Error (PAE) of the top five predicted structure of 12-HicA toxin. PAE estimates the relative positions of structural domains. The color at each point in the PAE plot represents the expected positional error of a residue on the predicted structure when aligned with a residue on the true structure. Lower PAE values indicate more accurate predictions of relative positions, while higher PAE values suggest uncertainty in the relative positions of residues from different structural domains in the 3D structure.
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/proof-figure6.png'
            alt='Figure 6. Structural alignment. The yellow structure represents the known structure of mRNA interferase toxin HICA, while the green one represents the predicted structure.'
            class='content-figure'
            style='width:42%'
            />
            <p class='content-legend'>
            Figure 6. Structural alignment. The yellow structure represents the known structure of mRNA interferase toxin HICA, while the green one represents the predicted structure.
            </p>
            <br/>
            <img
            src='https://static.igem.wiki/teams/4826/wiki/proof-figure7.png'
            alt='Figure 7. HicA toxin is toxic to E. coli. Genes were cloned into pBAD24, transformed into DH5-α, serially diluted, and plated onto agar in induction conditions using 0.2% arabinose (right panel) or non-inducing conditions (left panel). Tse2, a known Type VI Secretion System effector, was used as a positive control, and the negative control employed was a pBAD24 vector.'
            class='content-figure'
            />
            <p class='content-legend'>
            Figure 7. HicA toxin is toxic to E. coli. Genes were cloned into pBAD24, transformed into DH5-α, serially diluted, and plated onto agar in induction conditions using 0.2% arabinose (right panel) or non-inducing conditions (left panel). Tse2, a known Type VI Secretion System effector, was used as a positive control, and the negative control employed was a pBAD24 vector.
            </p>
            <br/>
            <p>
            We synthesized and cloned genes of interest in pBAD24 vectors, transformed them into E. coli, and induced expression using arabinose. We employed a known antibacterial toxin, Tse2, as a positive control, and the pBAD24 vector as a negative control. While we predicted 12-HicA toxin, PIN and YoeB were potential effector genes. In uninduced plates, the transformed strains exhibited rapid growth. However, in induced plates, the growth of E. coli transformed with 12-HicA toxin on a plasmid was severely inhibited. This confirms that the effector gene of HicA_toxin pfam Operon possesses toxin functionality. This validation underscores the reliability and effectiveness of our prediction tools.
            </p>
            <br/>
            <p>
            However, PIN and YeoB did not exhibit toxicity after induction. We speculate that they may not be effector genes or may not function as a toxin.
            </p>
              `}
        />
        <p className="subtitle" key={"Proof-2"} id="Proof-2">
          Online Platform Usage Test:
        </p>
        <ContentCard
          content={` 
            <img
            src='https://static.igem.wiki/teams/4826/wiki/proof-figure8.jpeg'
            alt='Figure 8. Users can access data from the BioRocket database online. For the afp13 homologous protein, it is also possible to view its programmable region.'
            class='content-figure'
            />
            <p class='content-legend'>
            Figure 8. Users can access data from the BioRocket database online. For the afp13 homologous protein, it is also possible to view its programmable region.
            </p>
            <br/>
            <img
            src='https://static.igem.wiki/teams/4826/wiki/proof-figure9.png'
            alt='Figure 9. Users can submit their own genomic data for real-time eCIS mining through the BioRocket cloud server.'
            class='content-figure'
            />
            <p class='content-legend'>
            Figure 9. Users can submit their own genomic data for real-time eCIS mining through the BioRocket cloud server.
            </p>
              `}
        />
        <p className="subtitle" key={"Proof-3"} id="Proof-3">
          • Command line operation:
        </p>
        <ContentCard
          content={` 
            <p>
            In addition to provide the web interface version, we also provide the command line data analysis process, see the GitLab repository for the code. The program can run smoothly on the computer with the following configuration.
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/proof-figure10.png'
            alt='Figure 10. Computer configuration'
            class='content-figure'
            style='width:36%'
            />
            <p class='content-legend'>
            Figure 10. Computer configuration
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/proof-figure11.png'
            alt='Figure 11. Configure the program's runtime environment.'
            class='content-figure'
            style='width:70%'
            />
            <p class='content-legend'>
            Figure 11. Configure the program's runtime environment.
            </p>
            <br/>
            <div style="text-align:center">
            <img
            src='https://static.igem.wiki/teams/4826/wiki/proof-figure12.png'
            alt='Figure 12. BioRocket ran successfully.'
            class='content-figure'
            style='width:70%'
            />
            <p class='content-legend'>
            Figure 12. BioRocket ran successfully.
            </p>
              `}
        />
        <p className="reference-title">• Reference:</p>
        <ContentCard
          content="
          <p class='reference'>
          [1] Geller AM, Pollin I, Zlotkin D, et al. The extracellular contractile injection system is enriched in environmental microbes and associates with numerous toxins. Nat Commun. 2021;12(1):3743. Published 2021 Jun 18. doi:10.1038/s41467-021-23777-7
          </p>
          <p class='reference'>
          [2] Varadi M, Anyango S, Deshpande M, et al. AlphaFold Protein Structure Database: massively expanding the structural coverage of protein-sequence space with high-accuracy models. Nucleic Acids Res. 2022;50(D1):D439-D444. doi:10.1093/nar/gkab1061
          </p>
          <p class='reference'>
          [3] Mariani V, Biasini M, Barbato A, Schwede T. lDDT: a local superposition-free score for comparing protein structures and models using distance difference tests. Bioinformatics. 2013;29(21):2722-2728. doi:10.1093/bioinformatics/btt473
          </p>
        "
        />
      </div>
      <Footer />
    </div>
  );
}
